<template>
  <b-modal
    id="epo-select-inactive-modal"
    v-model="localVisible"
    modal-class="epo-si-modal"
    dialog-class="epo-si-modal__dialog"
    content-class="epo-si-modal__content border-0"
    body-class="epo-si-modal__body p-0"
    centered
    hide-header
    hide-footer
    size="lg"
    @hidden="onHidden"
  >
    <div class="epo-si">
      <div class="epo-si__head">
        <h2 class="epo-si__title">
          {{ $t('lbl_EMPBE_EPO_SelectInactive_Title', { exceed: excessCount, count: excessCount }) }}
        </h2>
        <button type="button" class="epo-si__close" aria-label="Close" @click="close">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>

      <p class="epo-si__body">{{ $t('lbl_EMPBE_EPO_SelectInactive_Body', { new: limit }) }}</p>

      <div class="epo-si__suggestion">
        <img
          class="epo-si__suggestion-icon"
          src="https://cdn.adsalecdn.com/EMP/Backend/Images/light_bulb.svg"
          width="20"
          height="20"
          alt=""
        />
        <div class="epo-si__suggestion-text">
          <div class="epo-si__suggestion-label">{{ $t('lbl_EMPBE_EPO_SelectInactive_TipLabel') }}</div>
          <div class="epo-si__suggestion-content">{{ $t('lbl_EMPBE_EPO_SelectInactive_Hint') }}</div>
        </div>
      </div>

      <div v-if="loadingOptions" class="epo-si__loading">
        <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
      </div>
      <div v-else-if="!options.length" class="epo-si__empty">—</div>
      <div v-else class="epo-si__list" role="list">
        <label
          v-for="row in options"
          :key="row.entityId"
          class="epo-si__row"
          :class="{ 'epo-si__row--checked': checkedIds.includes(row.entityId) }"
          role="listitem"
        >
          <span class="epo-si__check">
            <input
              type="checkbox"
              class="epo-si__checkbox"
              :value="row.entityId"
              v-model="checkedIds"
            />
            <span class="epo-si__check-ui" aria-hidden="true"></span>
          </span>
          <span class="epo-si__avatar" aria-hidden="true">{{ avatarLetter(row) }}</span>
          <span class="epo-si__person">
            <span class="epo-si__name-line">
              <span class="epo-si__name">{{ displayName(row) }}</span>
              <span v-if="row.isContactPerson" class="epo-si__tag epo-si__tag--contact">
                {{ $t('EMP_UserSetting_ContactPerson') }}
              </span>
              <span v-if="row.isBoothRep" class="epo-si__tag epo-si__tag--rep">
                {{ $t('lbl_EMPBE_eBoothRepresentative') }}
              </span>
              <span class="epo-si__role">({{ roleLabel(row.userRole) }})</span>
            </span>
            <span class="epo-si__meta">{{ metaLine(row) }}</span>
            <span v-if="row.lastUsedAt" class="epo-si__last">
              {{ $t('lbl_EMPBE_EPO_SelectInactive_LastUsed', { date: row.lastUsedAt }) }}
            </span>
          </span>
        </label>
      </div>

      <p v-if="validationError" class="epo-si__error">{{ validationError }}</p>

      <div class="epo-si__footer">
        <button
          type="button"
          class="epo-si__btn epo-si__btn--delete"
          :disabled="busy || loadingOptions"
          :t_label="'EMPBE_EPO_SelectInactive_Delete' + epoTrackSuffix"
          @click="onDelete"
        >
          <i v-if="deleting" class="fa fa-spinner fa-spin" aria-hidden="true"></i>
          {{ $t('lbl_EMPBE_EPO_SelectInactive_Delete') }}
        </button>
        <div class="epo-si__actions">
          <span class="epo-si__selected">
            {{ $t('lbl_EMPBE_EPO_SelectInactive_Selected', { selected: checkedIds.length, count: checkedIds.length }) }}
          </span>
          <button
            type="button"
            class="epo-si__btn epo-si__btn--ghost"
            :disabled="busy"
            :t_label="'EMPBE_EPO_SelectInactive_Cancel' + epoTrackSuffix"
            @click="close"
          >
            {{ $t('lbl_EMPBE_EPO_SelectInactive_Cancel') }}
          </button>
          <button
            type="button"
            class="epo-si__btn epo-si__btn--primary"
            :disabled="busy || loadingOptions"
            :t_label="'EMPBE_EPO_SelectInactive_Confirm' + epoTrackSuffix"
            @click="onSave"
          >
            <i v-if="saving" class="fa fa-spinner fa-spin" aria-hidden="true"></i>
            {{ $t('lbl_EMPBE_EPO_SelectInactive_Confirm') }}
          </button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { epoService } from '../_services/epo.service';
import { resolveApiErrorMessage } from '../_helpers/apiResponse';
import { AlertDialog } from '../_helpers/AlertDialog';

function normalizeOption(row) {
  return {
    entityId: Number(row.entityId ?? row.EntityId),
    userRole: String(row.userRole ?? row.UserRole ?? ''),
    personNameSc: row.personNameSc ?? row.PersonNameSc ?? '',
    personNameEn: row.personNameEn ?? row.PersonNameEn ?? '',
    jobTitleSc: row.jobTitleSc ?? row.JobTitleSc ?? '',
    jobTitleEn: row.jobTitleEn ?? row.JobTitleEn ?? '',
    emailValue: row.emailValue ?? row.EmailValue ?? '',
    phoneValue: row.phoneValue ?? row.PhoneValue ?? '',
    isBoothRep: !!(row.isBoothRep ?? row.IsBoothRep),
    isContactPerson: !!(row.isContactPerson ?? row.IsContactPerson),
    lastUsedAt: row.lastUsedAt ?? row.LastUsedAt ?? '',
    targetRoleLimit: Number(row.targetRoleLimit ?? row.TargetRoleLimit ?? 0),
  };
}

export default {
  name: 'EpoRoleInactiveModal',
  mixins: [AlertDialog],
  props: {
    value: { type: Boolean, default: false },
    excessCount: { type: Number, default: 0 },
    targetRoleLimit: { type: Number, default: 0 },
  },
  data() {
    return {
      loadingOptions: false,
      saving: false,
      deleting: false,
      options: [],
      checkedIds: [],
      validationError: '',
      resolvedLimit: 0,
    };
  },
  computed: {
    epoTrackSuffix() {
      const u = this.$store?.state?.authentication?.user || {};
      const companyId = u.empCompanyId || u.EmpCompanyId || 0;
      const roleUserId = u.accountUserId || u.AccountUserId || 0;
      return `_${companyId}_${roleUserId}`;
    },
    localVisible: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit('input', v);
      },
    },
    busy() {
      return this.saving || this.deleting;
    },
    limit() {
      if (this.targetRoleLimit > 0) return this.targetRoleLimit;
      if (this.resolvedLimit > 0) return this.resolvedLimit;
      const first = this.options[0];
      return first ? Number(first.targetRoleLimit ?? 0) : 0;
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.validationError = '';
        this.loadOptions();
      }
    },
    checkedIds() {
      this.validationError = '';
    },
  },
  methods: {
    close() {
      this.localVisible = false;
    },
    onHidden() {
      this.$emit('close');
    },
    displayName(row) {
      const langId = String(localStorage.getItem('currentLangId') || '1252');
      if (langId === '936' || langId === '950') {
        return row.personNameSc || row.personNameEn || `#${row.entityId}`;
      }
      return row.personNameEn || row.personNameSc || `#${row.entityId}`;
    },
    jobTitle(row) {
      const langId = String(localStorage.getItem('currentLangId') || '1252');
      if (langId === '936' || langId === '950') {
        return row.jobTitleSc || row.jobTitleEn || '';
      }
      return row.jobTitleEn || row.jobTitleSc || '';
    },
    metaLine(row) {
      return [this.jobTitle(row), row.phoneValue, row.emailValue].filter(Boolean).join(' • ');
    },
    avatarLetter(row) {
      const name = String(this.displayName(row) || '').trim();
      return name ? name.charAt(0).toUpperCase() : '?';
    },
    roleLabel(role) {
      const r = String(role || '').toUpperCase();
      if (r === 'ADMIN') return this.$t('EMP_UserSetting_Role_Admin');
      if (r === 'MEMBER') return this.$t('EMP_UserSetting_Role_Member');
      return role || '';
    },
    async loadOptions() {
      this.loadingOptions = true;
      try {
        const res = await epoService.GetRoleInactiveOptions();
        if (!res?.isSuccessful) return;
        const rows = Array.isArray(res.data) ? res.data : (res.data ? [res.data] : []);
        this.options = rows.map(normalizeOption).filter((r) => r.entityId > 0);
        this.checkedIds = rows
          .filter((r) => !!(r.isChecked ?? r.IsChecked))
          .map((r) => Number(r.entityId ?? r.EntityId))
          .filter((id) => id > 0);
        if (this.options[0]?.targetRoleLimit != null) {
          this.resolvedLimit = Number(this.options[0].targetRoleLimit);
        }
      } finally {
        this.loadingOptions = false;
      }
    },
    /** 自由处理：不强制一次勾满超额人数，仅要求至少选 1 位 */
    validateSelection() {
      if (this.checkedIds.length > 0) {
        this.validationError = '';
        return true;
      }
      this.validationError = this.$t('lbl_EMPBE_EPO_SelectInactive_ValidationErrorMin');
      return false;
    },
    async onSave() {
      if (!this.validateSelection()) return;

      /* Figma 3385:420599 — 停用 N 个账号？ */
      const selected = this.checkedIds.length;
      const confirm = await this.showCustomConfirmADD(
        this.$t('lbl_EMPBE_EPO_SelectInactive_ConfirmTitle', { selected, count: selected }),
        this.$t('lbl_EMPBE_EPO_SelectInactive_ConfirmBody'),
        this.$t('lbl_EMPBE_EPO_SelectInactive_Confirm'),
        this.$t('lbl_EMPBE_EPO_SelectInactive_Cancel'),
      );
      if (!(confirm && confirm.value)) return;

      this.saving = true;
      try {
        const entityIds = this.checkedIds.join(',');
        const langId = Number(localStorage.getItem('currentLangId') || 1252);
        const res = await epoService.SetRoleInactive({ entityIds, langId });
        if (!res?.isSuccessful) {
          this.validationError = resolveApiErrorMessage(
            res,
            this.$t('lbl_EMPBE_EPO_SelectInactive_ValidationErrorMin'),
          );
          return;
        }
        this.localVisible = false;
        this.showSubmitSucess(
          this.$t('lbl_EMPBE_EPO_SelectInactive_SuccessTitle'),
          this.$t('lbl_EMPBE_EPO_SelectInactive_Success', { count: selected }),
        );
        this.$emit('saved');
      } catch (e) {
        this.validationError = e?.message
          || this.$t('lbl_EMPBE_EPO_SelectInactive_ValidationErrorMin');
      } finally {
        this.saving = false;
      }
    },
    async onDelete() {
      if (!this.checkedIds.length) {
        this.validationError = this.$t('lbl_EMPBE_EPO_SelectInactive_ValidationErrorMin');
        return;
      }
      const selected = this.checkedIds.length || 1;
      const title = this.$t('lbl_EMPBE_EPO_SelectInactive_DeleteTitle', { count: selected });
      const text = this.$t('lbl_EMPBE_EPO_SelectInactive_DeleteBody');
      const confirmButton = this.$t('lbl_EMPBE_EPO_SelectInactive_Delete');
      const cancelButton = this.$t('lbl_EMPBE_EPO_SelectInactive_Cancel');
      const result = await this.showCustomConfirmDEL(title, text, confirmButton, cancelButton);
      if (!(result && result.value)) return;

      this.deleting = true;
      this.validationError = '';
      try {
        const langId = Number(localStorage.getItem('currentLangId') || 1252);
        const res = await epoService.DeleteRoles({
          entityIds: this.checkedIds.join(','),
          langId,
        });
        if (!res?.isSuccessful) {
          this.validationError = resolveApiErrorMessage(
            res,
            this.$t('lbl_EMPBE_DeleteFailed'),
          );
          return;
        }

        await this.loadOptions();
        this.$emit('saved');
        this.showSubmitSucess(
          this.$t('lbl_EMPBE_EPO_SelectInactive_DeleteSuccessTitle'),
          this.$t('lbl_EMPBE_EPO_SelectInactive_DeleteSuccessBody', { count: selected }),
        );
        if (Math.max(0, this.options.length - this.limit) === 0) {
          this.localVisible = false;
        }
      } catch (e) {
        this.validationError = e?.message || this.$t('lbl_EMPBE_DeleteFailed');
      } finally {
        this.deleting = false;
      }
    },
  },
};
</script>

<style lang="scss">
.epo-si-modal {
  .modal-dialog {
    max-width: 800px;
  }
  .modal-content {
    border: none;
    border-radius: 24px;
    overflow: hidden;
    box-shadow: 0 2px 15px rgba(183, 183, 183, 0.3);
  }
  .modal-body {
    padding: 0 !important;
  }
  /* 底部「删除 / 取消 / 设为停用」强制 16px，避免被全局 button 样式覆盖 */
  .epo-si__btn,
  .epo-si__btn--delete,
  .epo-si__btn--ghost,
  .epo-si__btn--primary {
    font-size: 16px !important;
  }
}
</style>

<style lang="scss" scoped>
.epo-si {
  --epo-purple: #7366ff;
  --epo-ink: rgba(0, 0, 0, 0.8);
  --epo-muted: rgba(0, 0, 0, 0.6);
  --epo-faint: rgba(0, 0, 0, 0.4);
  --epo-line: rgba(0, 0, 0, 0.1);
  padding: 32px;
  background: #fff;
}

.epo-si__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.epo-si__title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
  color: var(--epo-ink);
}

.epo-si__close {
  appearance: none;
  border: none;
  background: transparent;
  font-size: 28px;
  line-height: 1;
  color: var(--epo-faint);
  cursor: pointer;
  padding: 0 4px;
}

.epo-si__body {
  margin: 0 0 16px;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.3;
  color: var(--epo-muted);
}

.epo-si__suggestion {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  margin: 0 0 16px;
  border-radius: 8px;
  background: #e7f8ff;
}

.epo-si__suggestion-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  margin-top: 1px;
  display: block;
}

.epo-si__suggestion-text {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.epo-si__suggestion-label {
  font-size: 11px;
  font-weight: 500;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.4);
}

.epo-si__suggestion-content {
  font-size: 14px;
  font-weight: 400;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.8);
}

.epo-si__selected {
  font-size: 14px;
  font-weight: 400;
  color: var(--epo-faint); /* rgba(0, 0, 0, .4) */
  white-space: nowrap;
}

.epo-si__list {
  max-height: 420px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
  padding-right: 4px;
}

.epo-si__row {
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 0;
  padding: 16px;
  border: 1px solid var(--epo-line);
  border-radius: 8px;
  cursor: pointer;
  background: #fff;

  &--checked {
    border-color: rgba(115, 102, 255, 0.35);
    background: rgba(115, 102, 255, 0.04);
  }
}

.epo-si__check {
  position: relative;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
}

.epo-si__checkbox {
  position: absolute;
  inset: 0;
  opacity: 0;
  margin: 0;
  cursor: pointer;
  z-index: 1;
}

.epo-si__check-ui {
  display: block;
  width: 18px;
  height: 18px;
  border-radius: 2px;
  border: 2px solid rgba(0, 0, 0, 0.15);
  box-sizing: border-box;
  background: #fff;
}

.epo-si__checkbox:checked + .epo-si__check-ui {
  background: var(--epo-purple);
  border-color: var(--epo-purple);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='none' stroke='%23fff' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round' d='M3.5 8.5l3 3 6-6'/%3E%3C/svg%3E");
  background-size: 12px 12px;
  background-repeat: no-repeat;
  background-position: center;
}

.epo-si__avatar {
  flex-shrink: 0;
  width: 68px;
  height: 68px;
  border-radius: 99px;
  border: 1px solid var(--epo-purple);
  background: rgba(115, 102, 255, 0.08);
  color: var(--epo-purple);
  font-size: 28px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.epo-si__person {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.epo-si__name-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.epo-si__name {
  font-size: 16px;
  font-weight: 700;
  color: var(--epo-ink);
}

.epo-si__tag {
  font-size: 12px;
  font-weight: 600;
  padding: 4px 6px;
  border-radius: 25px;
  color: var(--epo-ink);

  &--contact {
    background: #f1e7ff;
  }

  &--rep {
    background: #ffe7e7;
  }
}

.epo-si__role {
  font-size: 12px;
  font-style: italic;
  color: var(--epo-faint);
}

.epo-si__meta {
  font-size: 14px;
  line-height: 1.3;
  color: var(--epo-muted);
}

.epo-si__last {
  font-size: 12px;
  color: var(--epo-muted);
}

.epo-si__error {
  margin: 0 0 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fff0f0;
  color: #cd1515;
  font-size: 13px;
}

.epo-si__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-top: 8px;
}

.epo-si__actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
}

.epo-si__btn {
  appearance: none;
  border: 1px solid transparent;
  cursor: pointer;
  box-sizing: border-box;
  height: 40px;
  border-radius: 8px;
  font-size: 16px !important;
  font-weight: 600;
  line-height: 1.2;
  padding: 0 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  &--ghost {
    background: transparent;
    color: var(--epo-muted);

    &:hover:not(:disabled),
    &:focus:not(:disabled) {
      background: rgba(0, 18, 26, 0.05);
    }
  }

  &--primary {
    background: var(--epo-purple);
    border-color: var(--epo-purple);
    color: #fff;
  }

  &--delete {
    background: transparent;
    color: #dc3545;

    &:hover:not(:disabled),
    &:focus:not(:disabled) {
      background: rgba(0, 18, 26, 0.05);
    }
  }
}

.epo-si__loading,
.epo-si__empty {
  padding: 28px 16px;
  text-align: center;
  color: var(--epo-faint);
  border: 1px dashed var(--epo-line);
  border-radius: 8px;
  margin-bottom: 16px;
}

@media (max-width: 576px) {
  .epo-si {
    padding: 20px 16px;
  }
  .epo-si__footer {
    flex-direction: column;
    align-items: stretch;
  }
  .epo-si__actions {
    justify-content: flex-end;
    flex-wrap: wrap;
  }
  .epo-si__selected {
    width: 100%;
    text-align: right;
  }
  .epo-si__avatar {
    width: 48px;
    height: 48px;
    font-size: 20px;
  }
}
</style>
